"use client";

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@ui/components/accordion";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import { ArrowRightIcon, ChevronDownIcon } from "lucide-react";
import { type ComponentType, type ReactNode, useEffect, useRef } from "react";

/**
 * The marketing site's mobile menu, as it sits inside the NavBar's Sheet.
 *
 * It owns its own layout and scrolling: a header row, one scroll region, and
 * a footer pinned to the bottom that always shows the account buttons. The
 * parent Sheet must be a full-height flex column (`h-dvh flex flex-col p-0`)
 * with no overflow of its own, or the pinned footer scrolls away again.
 *
 * Dropdown groups (Products, Solutions, Resources) become accordion rows,
 * collapsed by default. The group that holds the current page opens on
 * mount and the scroll region jumps to the current page's row, so the
 * visitor sees where they are. Standalone links (Pricing, Docs) are plain
 * rows under the groups.
 */

export interface NavMenuItem {
	label: string;
	href: string;
	/** Shown on desktop only; the mobile rows stay one line. */
	description?: string;
	icon?: ReactNode;
	onClick?: () => void;
}

export interface NavMenuSection {
	heading?: string;
	items: NavMenuItem[];
	/** A tinted, visually distinct section (the "For PR teams" group). */
	featured?: boolean;
	/** One line under a featured heading; desktop only. */
	blurb?: string;
	/** An optional "see more" link under the section's items. */
	footerLink?: { label: string; href: string };
}

export interface NavMenuGroup {
	key: string;
	label: string;
	sections: NavMenuSection[];
}

export type NavLinkComponent = ComponentType<{
	href: string;
	className?: string;
	onClick?: () => void;
	prefetch?: boolean;
	"aria-current"?: "page";
	children?: ReactNode;
}>;

export interface MobileNavMenuProps {
	/** Products, Solutions, Resources: each renders as an accordion row. */
	groups: NavMenuGroup[];
	/** Pricing, Docs: plain rows under the groups. */
	standaloneItems: NavMenuItem[];
	isMenuItemActive: (href: string) => boolean;
	signedIn: boolean;
	labels: { login: string; signup: string; dashboard: string };
	/** Link for marketing routes (NavBar passes LocaleLink). */
	linkComponent: NavLinkComponent;
	/** Link for /auth and /app routes (NavBar passes NextLink). */
	authLinkComponent?: NavLinkComponent;
	authHrefs?: { login?: string; signup?: string; dashboard?: string };
	/** Header row content, e.g. the logo and the sheet's title. */
	header?: ReactNode;
	/** Called when any link is tapped, so the sheet can close. */
	onNavigate?: () => void;
	className?: string;
}

const ROW =
	"flex min-h-12 w-full items-center gap-3 rounded-lg px-3 text-left text-base font-medium transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring";

const SUBHEADING =
	"px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";

function groupHasActiveItem(
	group: NavMenuGroup,
	isMenuItemActive: (href: string) => boolean,
) {
	return group.sections.some((section) =>
		section.items.some((item) => isMenuItemActive(item.href)),
	);
}

export function MobileNavMenu({
	groups,
	standaloneItems,
	isMenuItemActive,
	signedIn,
	labels,
	linkComponent: Link,
	authLinkComponent,
	authHrefs,
	header,
	onNavigate,
	className,
}: MobileNavMenuProps) {
	const AuthLink = authLinkComponent ?? Link;
	const scrollRef = useRef<HTMLElement>(null);

	// On open, bring the current page's row into view inside the scroll
	// region (never the page itself). The row can sit far down an expanded
	// group, e.g. Media Lists at the end of Products. Runs once on mount:
	// the Sheet unmounts this component when it closes.
	useEffect(() => {
		const frame = requestAnimationFrame(() => {
			const region = scrollRef.current;
			const current = region?.querySelector<HTMLElement>(
				'[aria-current="page"]',
			);
			if (!region || !current) {
				return;
			}
			const regionBox = region.getBoundingClientRect();
			const box = current.getBoundingClientRect();
			if (box.top >= regionBox.top && box.bottom <= regionBox.bottom) {
				return;
			}
			const offset = box.top - regionBox.top + region.scrollTop;
			region.scrollTop = offset - (region.clientHeight - box.height) / 2;
		});
		return () => cancelAnimationFrame(frame);
	}, []);
	const activeGroup = groups.find((group) =>
		groupHasActiveItem(group, isMenuItemActive),
	);

	const hrefs = {
		login: authHrefs?.login ?? "/auth/login",
		signup: authHrefs?.signup ?? "/auth/signup",
		dashboard: authHrefs?.dashboard ?? "/app",
	};

	const renderItem = (item: NavMenuItem) => {
		const active = isMenuItemActive(item.href);
		return (
			<Link
				key={item.href}
				href={item.href}
				aria-current={active ? "page" : undefined}
				onClick={() => {
					item.onClick?.();
					onNavigate?.();
				}}
				className={cn(
					"flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
					active
						? "bg-primary/10 font-semibold text-foreground"
						: "text-foreground/80",
				)}
			>
				{item.icon ? (
					<span
						aria-hidden
						className="flex size-4 shrink-0 items-center justify-center"
					>
						{item.icon}
					</span>
				) : null}
				<span className="min-w-0 truncate">{item.label}</span>
			</Link>
		);
	};

	return (
		<div
			className={cn(
				"flex h-full min-h-0 w-full flex-col bg-background",
				className,
			)}
		>
			{/* pr-12 leaves room for the Sheet's own close button. */}
			<div className="flex h-14 shrink-0 items-center border-b px-4 pr-12">
				{header}
			</div>

			<nav
				ref={scrollRef}
				aria-label="Main"
				className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2"
			>
				<Accordion
					type="single"
					collapsible
					defaultValue={activeGroup?.key}
				>
					{groups.map((group) => {
						const active = activeGroup?.key === group.key;
						return (
							<AccordionItem
								key={group.key}
								value={group.key}
								className="border-border/60"
							>
								<AccordionTrigger
									className={cn(
										ROW,
										"py-0 hover:no-underline",
										active
											? "font-semibold text-foreground"
											: "text-foreground/80",
									)}
								>
									{group.label}
									<ChevronDownIcon
										aria-hidden
										className="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
									/>
								</AccordionTrigger>
								<AccordionContent className="[&>div]:pb-2">
									<div className="flex flex-col gap-0.5">
										{group.sections.map((section, si) => (
											<div
												key={section.heading ?? si}
												className={cn(
													section.featured &&
														"mx-1 mt-2 rounded-lg bg-primary/5 p-1 ring-1 ring-primary/15 ring-inset",
												)}
											>
												{section.heading ? (
													<div
														className={cn(
															SUBHEADING,
															section.featured &&
																"pt-2 text-primary",
														)}
													>
														{section.heading}
													</div>
												) : null}
												<div className="flex flex-col gap-0.5">
													{section.items.map(
														renderItem,
													)}
												</div>
												{section.footerLink ? (
													<Link
														href={
															section.footerLink
																.href
														}
														onClick={onNavigate}
														className="flex min-h-11 items-center gap-1.5 rounded-lg px-3 font-medium text-primary text-sm transition-colors hover:bg-primary/10 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
													>
														{
															section.footerLink
																.label
														}
														<ArrowRightIcon
															aria-hidden
															className="size-3.5"
														/>
													</Link>
												) : null}
											</div>
										))}
									</div>
								</AccordionContent>
							</AccordionItem>
						);
					})}
				</Accordion>

				{standaloneItems.map((item) => {
					const active = isMenuItemActive(item.href);
					return (
						<div
							key={item.href}
							className="border-border/60 border-b last:border-b-0"
						>
							<Link
								href={item.href}
								aria-current={active ? "page" : undefined}
								onClick={() => {
									item.onClick?.();
									onNavigate?.();
								}}
								className={cn(
									ROW,
									active
										? "font-semibold text-foreground"
										: "text-foreground/80",
								)}
							>
								{item.label}
							</Link>
						</div>
					);
				})}
			</nav>

			{/* Pinned: the account buttons never scroll out of reach. */}
			<div className="flex shrink-0 flex-col gap-2 border-t bg-background px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
				{signedIn ? (
					<Button
						asChild
						size="lg"
						variant="primary"
						className="w-full"
					>
						<AuthLink href={hrefs.dashboard} onClick={onNavigate}>
							{labels.dashboard}
						</AuthLink>
					</Button>
				) : (
					<>
						<Button
							asChild
							size="lg"
							variant="primary"
							className="w-full"
						>
							<AuthLink
								href={hrefs.signup}
								prefetch
								onClick={onNavigate}
							>
								{labels.signup}
							</AuthLink>
						</Button>
						{/* Signing in is a different job from signing up,
						    and an SSO user has no signup to do. */}
						<Button
							asChild
							size="lg"
							variant="outline"
							className="w-full"
						>
							<AuthLink href={hrefs.login} onClick={onNavigate}>
								{labels.login}
							</AuthLink>
						</Button>
					</>
				)}
			</div>
		</div>
	);
}
